/** Shared sidebar for Findings, endpoint inventory, and Security. Settings has its own console (components/settings). */
import { hasEntitlement } from "../lib/entitlements.js";
import { securityTabEnabled } from "../lib/featureFlags.js";
import { phAttrs } from "../lib/analytics/attrs.js";
import { ATTRIBUTE } from "../lib/analytics/surfaceVocabulary.js";
import { allowsGate, useMyPermissions } from "../lib/permissions.js";
import { ADMIN_VIEW_MODES, MODE_IDS, shownViewModes } from "../lib/routeModel.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { EYEBROW_CLASS } from "../lib/ui.js";

const MODE_LABELS = {
    overview: { rail: "O", label: "Overview" },
    explore: { rail: "E", label: "Explore" },
    workspace: { rail: "W", label: "Investigations" },
    dashboard: { rail: "0", label: "Chat" },
    triage: { rail: "T", label: "Findings" },
    security: { rail: "2", label: "Security" },
    settings: { rail: "6", label: "Settings" },
    endpoints: { rail: "4", label: "Fleet" },
    governance: { rail: "5", label: "Governance" },
};

// The ids and their order come from routeModel.js, which the analytics layer
// reads too.
export const MODES = MODE_IDS.map((id) => ({ id, ...MODE_LABELS[id] }));

const ALL_ADMIN_VIEWS = [
    { id: "security-overview", label: "Overview", note: "Findings across your organization and saved investigations", permission: PERMISSIONS.AI_SESSIONS_READ },
    { id: "discovery", label: "Discovery", note: "Explore review findings across visible activity", permission: PERMISSIONS.AI_SESSIONS_READ },
    { id: "alerts", label: "Investigations", note: "Review saved sets of findings and their evidence", permission: PERMISSIONS.AI_SESSIONS_READ },
    { id: "triage", label: "Findings", permission: PERMISSIONS.AI_SESSIONS_READ },
    { id: "endpoints", label: "Agents and models", note: "What AI is present and used across your organization", permission: { any: [PERMISSIONS.ENDPOINT_METADATA_READ, PERMISSIONS.AI_SESSIONS_READ] } },
].map((view) => ({ ...view, mode: ADMIN_VIEW_MODES[view.id] }));

// The views this build shows, with their modes. The analytics layer reads
// the same flags through routeModel.js.
export const SHOWN_VIEW_MODES = shownViewModes({ securityEnabled: securityTabEnabled() });

export const ADMIN_VIEWS = ALL_ADMIN_VIEWS.filter((v) => v.id in SHOWN_VIEW_MODES);

// The RBAC permission gate a view's nav entry carries, or null if it's
// visible to everyone. May be a single permission string OR an `{ any:
// [...] }` OR-gate (Fleet's overview reads either of two surfaces). Single
// source of truth shared by the nav (AdminMenu) and the direct-URL guard
// (AdminPage).
export function permissionForView(viewId) {
    return ADMIN_VIEWS.find((v) => v.id === viewId)?.permission ?? null;
}

// Resolve a view's permission gate against the caller's posture; see
// allowsGate in lib/permissions.js, which Settings' sections use too.
export function allowsView(permission, caps) {
    return allowsGate(permission, caps);
}

// Admin views for `mode`, filtered to those the caller's permission set
// allows. Pure (caps in, list out) so the per-role nav matrix is unit
// testable without rendering. `allowsView` fails open while caps are
// loading/unavailable, so a pre-RBAC tenant sees the full nav exactly as
// before. Entitlement-gated views still require a confirmed grant.
export function visibleAdminViews(views, mode, caps, entitlements = null) {
    return views.filter(
        (v) => v.mode === mode && v.menu !== false && allowsView(v.permission, caps)
            && (!v.entitlement || hasEntitlement(entitlements, v.entitlement)),
    );
}

// Mode of a given admin view id, or null if the id isn't a registered
// admin view. The caller handles the homepage fallback.
export function modeForView(viewId) {
    const def = ADMIN_VIEWS.find((v) => v.id === viewId);
    return def?.mode || null;
}

// First view registered for a given mode. Used when the rail mode
// button is clicked: we land on this view as the default destination.
// Returns null for modes without admin views.
export function firstViewForMode(modeId) {
    const def = ADMIN_VIEWS.find((v) => v.mode === modeId && v.menu !== false);
    return def?.id || null;
}

export function firstVisibleViewForMode(modeId, caps, entitlements = null) {
    return visibleAdminViews(ADMIN_VIEWS, modeId, caps, entitlements)[0]?.id || null;
}

// One of the mode's views, under its row in the sidebar (AppNav.jsx), the
// name in line with the row's.
function MenuRow({ view, isActive, onSelect }) {
    return (
        <button
            type="button"
            aria-current={isActive ? "page" : undefined}
            onClick={() => onSelect(view.id)}
            data-drawer-pick=""
            {...phAttrs({ [ATTRIBUTE.NAV]: view.id })}
            className={[
                "relative flex h-[32px] w-full appearance-none items-center rounded-[8px] border-0 px-[11.5px] text-left text-[13px] transition-colors cursor-pointer",
                isActive
                    ? "bg-[color:var(--green-soft)] text-[color:var(--ink)]"
                    : "bg-transparent text-[color:var(--muted)] hover:bg-[color-mix(in_srgb,var(--side-raise)_55%,transparent)] hover:text-[color:var(--ink)]",
            ].join(" ")}
        >
            <span className="truncate">{view.label}</span>
        </button>
    );
}

export default function AdminMenu({ activeView, entitlements = null, mode = "settings", onSelect }) {
    // The same definitions drive the grouped menu and direct-URL guard.
    const modeDef = MODES.find((m) => m.id === mode);
    // Permission-gated views are hidden when the caller's set lacks the
    // view's permission. While the set is still loading (or unavailable
    // on a pre-RBAC tenant) `hasPermission` fails open, so the nav looks
    // exactly as it did before RBAC rather than flash-hiding every row.
    const caps = useMyPermissions();
    const visibleViews = visibleAdminViews(ADMIN_VIEWS, mode, caps, entitlements);
    const groups = [...new Set(visibleViews.map((view) => view.group || ""))];

    return (
        <nav data-admin-menu className="grid gap-[2px]" aria-label={modeDef?.label + " navigation"}>
            {groups.map((group) => (
                <section key={group} aria-label={group || undefined} className="grid gap-[2px]">
                    {group ? (
                        <h2 className={`${EYEBROW_CLASS} m-0 px-[11.5px] pb-[4px] pt-[10px] font-normal`}>{group}</h2>
                    ) : null}
                    {visibleViews.filter((view) => (view.group || "") === group).map((view) => (
                        <MenuRow key={view.id} view={view} isActive={view.id === activeView} onSelect={onSelect} />
                    ))}
                </section>
            ))}
        </nav>
    );
}
